import { useMemo, useState } from 'react'
import {
  DEFAULT_PAGE_SIZE,
  Button,
  Card,
  DataTable,
  Field,
  Input,
  PageHead,
  Pager,
  Segmented,
  StateBlock,
  type Column,
} from '@w7/ui'
import { count, EMPTY, lll } from '@/lib/format'
import { MemberPicker } from '@/features/reports/MemberPicker'
import type { InvalidBetRow, InvalidBetsTab } from './invalidBetsTypes'
import { useInvalidBets } from './useInvalidBets'

/**
 * Invalid Bets — Winner All Logs (Master only).
 *
 * Filters and columns match winner-golive invalidbetlogs + VAR/Invalid
 * directives. Live: Agent-API `getVARBets` / `searchIBLogs`.
 * Member chips: `Member/searchHierarchyNew` via hierarchy MemberPicker.
 */

const PAGE_SIZE = DEFAULT_PAGE_SIZE

const TAB_ITEMS: { id: InvalidBetsTab; label: string }[] = [
  { id: 'var-bet', label: 'VARBet' },
  { id: 'invalid-bets', label: 'Invalid Bets' },
]

function todayInputValue(): string {
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

function daysAgoInputValue(days: number): string {
  const d = new Date()
  d.setDate(d.getDate() - days)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

export function InvalidBetsPage() {
  const [page, setPage] = useState(1)
  const [tab, setTab] = useState<InvalidBetsTab>('var-bet')
  const [members, setMembers] = useState<string[]>([])
  const [fromDate, setFromDate] = useState(() => daysAgoInputValue(7))
  const [toDate, setToDate] = useState(() => todayInputValue())
  const [applied, setApplied] = useState({
    tab: 'var-bet' as InvalidBetsTab,
    members: [] as string[],
    fromDate: daysAgoInputValue(7),
    toDate: todayInputValue(),
  })

  const { data, isLoading } = useInvalidBets({
    page,
    pageSize: PAGE_SIZE,
    tab: applied.tab,
    members: applied.members,
    fromDate: applied.fromDate,
    toDate: applied.toDate,
  })

  const memberRange = useMemo(
    () => ({ from: fromDate, to: toDate }),
    [fromDate, toDate],
  )

  const columns = useMemo<Column<InvalidBetRow>[]>(() => {
    const indexCol: Column<InvalidBetRow> = {
      id: 'index',
      header: '#',
      align: 'right',
      numeric: true,
      cell: (_row, index) => (page - 1) * PAGE_SIZE + index + 1,
    }

    if (applied.tab === 'var-bet') {
      return [
        indexCol,
        { id: 'player', header: 'Player', cell: (row) => row.playerName || EMPTY },
        { id: 'event', header: 'Event', cell: (row) => row.event || EMPTY },
        {
          id: 'placedAt',
          header: 'Place Date',
          cell: (row) => (row.placedAt ? lll(row.placedAt) : EMPTY),
        },
        {
          id: 'voidAt',
          header: 'Void Date',
          cell: (row) => (row.updatedAt ? lll(row.updatedAt) : EMPTY),
        },
        { id: 'type', header: 'Type', cell: (row) => row.type || EMPTY },
        {
          id: 'selection',
          header: 'Horse Name',
          cell: (row) => row.selectionName || EMPTY,
        },
        {
          id: 'odds',
          header: 'Odds',
          align: 'right',
          numeric: true,
          cell: (row) => row.odds,
        },
        {
          id: 'stake',
          header: 'Stake',
          align: 'right',
          numeric: true,
          cell: (row) => row.stake,
        },
        { id: 'placeIp', header: 'Bet Place IP', cell: (row) => row.placeIp || EMPTY },
      ]
    }

    return [
      indexCol,
      { id: 'player', header: 'Player', cell: (row) => row.playerName || EMPTY },
      {
        id: 'placedAt',
        header: 'Place Date',
        cell: (row) => (row.placedAt ? lll(row.placedAt) : EMPTY),
      },
      {
        id: 'invalidAt',
        header: 'Invalid Date',
        cell: (row) => (row.updatedAt ? lll(row.updatedAt) : EMPTY),
      },
      { id: 'type', header: 'Type', cell: (row) => row.type || EMPTY },
      {
        id: 'selection',
        header: 'Horse Name',
        cell: (row) => row.selectionName || EMPTY,
      },
      {
        id: 'odds',
        header: 'Odds',
        align: 'right',
        numeric: true,
        cell: (row) => row.odds,
      },
      {
        id: 'stake',
        header: 'Stake',
        align: 'right',
        numeric: true,
        cell: (row) => row.stake,
      },
      { id: 'invalidBy', header: 'Invalid By', cell: (row) => row.invalidBy || EMPTY },
      { id: 'placeIp', header: 'Bet Place IP', cell: (row) => row.placeIp || EMPTY },
      { id: 'invalidIp', header: 'Invalid IP', cell: (row) => row.invalidIp || EMPTY },
      {
        id: 'action',
        header: 'Action',
        cell: () => (
          <Button variant="secondary" size="sm" disabled>
            View
          </Button>
        ),
      },
    ]
  }, [applied.tab, page])

  const rows = data?.rows ?? []
  const total = data?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE) || 1)

  const getLogs = () => {
    setPage(1)
    setApplied({
      tab,
      members: [...members],
      fromDate,
      toDate,
    })
  }

  const clearFilters = () => {
    const from = daysAgoInputValue(7)
    const to = todayInputValue()
    setTab('var-bet')
    setMembers([])
    setFromDate(from)
    setToDate(to)
    setPage(1)
    setApplied({ tab: 'var-bet', members: [], fromDate: from, toDate: to })
  }

  const onTabChange = (next: InvalidBetsTab) => {
    setTab(next)
    setPage(1)
    setApplied((prev) => ({ ...prev, tab: next }))
  }

  return (
    <>
      <PageHead
        title="Invalid Bets"
        subtitle="VAR and invalid bet logs — Master All Logs from Winner"
      />

      <Card
        title="Filters"
        subtitle="Member, date range, and log type"
        actions={
          <span className="w7-row" style={{ gap: 8 }}>
            <Button variant="primary" onClick={getLogs}>
              Get Logs
            </Button>
            <Button variant="secondary" onClick={clearFilters}>
              Clear Filters
            </Button>
          </span>
        }
      >
        <div className="w7-row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: '1 1 220px' }}>
            <MemberPicker
              selected={members}
              onChange={setMembers}
              source="hierarchy"
              range={memberRange}
              rootId={null}
              label="Member"
              allLabel="All members"
            />
          </div>
          <div style={{ flex: '0 1 160px' }}>
            <Field label="From Date">
              {({ id }) => (
                <Input
                  id={id}
                  type="date"
                  value={fromDate}
                  onChange={(event) => setFromDate(event.target.value)}
                />
              )}
            </Field>
          </div>
          <div style={{ flex: '0 1 160px' }}>
            <Field label="To Date">
              {({ id }) => (
                <Input
                  id={id}
                  type="date"
                  value={toDate}
                  onChange={(event) => setToDate(event.target.value)}
                />
              )}
            </Field>
          </div>
          <div style={{ flex: '1 1 240px' }}>
            <Segmented
              label="Log type"
              items={TAB_ITEMS}
              value={tab}
              onChange={onTabChange}
            />
          </div>
        </div>
      </Card>

      <Card
        flush
        title={applied.tab === 'var-bet' ? 'VAR bets' : 'Invalid bets'}
        subtitle={isLoading ? 'Loading' : `${count(total)} in this list`}
        footer={
          <Pager
            page={data?.page ?? page}
            pageCount={pageCount}
            onChange={setPage}
            total={total}
            pageSize={PAGE_SIZE}
          />
        }
      >
        <DataTable
          label={applied.tab === 'var-bet' ? 'VAR bet logs' : 'Invalid bet logs'}
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          loading={isLoading}
          skeletonRows={8}
          empty={
            <StateBlock icon="sliders" title="No data found">
              {applied.tab === 'var-bet'
                ? 'No VAR bet rows for these filters.'
                : 'No invalid bet rows for these filters.'}
            </StateBlock>
          }
        />
      </Card>
    </>
  )
}
